import { BillableEvent } from 'types';
import { joinRsql, LogicalOperator } from 'utils';

import { downloadCsv } from './download-csv';
import { fetchPaginated } from './fetch-paginated';
import fetchWithRetry from './fetch-retry';

type BillableEventSpecification = { transactionId?: number | string; unitId?: number | string };

const appendSpecification = (filterString?: string, specification?: BillableEventSpecification) =>
  joinRsql(
    LogicalOperator.AND,
    filterString,
    specification?.transactionId
      ? `associatedTransaction.id==${specification.transactionId}`
      : !!specification?.unitId && `unit.id==${specification.unitId}`
  );

export const fetchBillableEventsWithSpecification =
  (specification?: BillableEventSpecification): ReturnType<typeof fetchPaginated<BillableEvent>> =>
  async params =>
    fetchPaginated<BillableEvent>('billableEvents')({
      ...params,
      filterString: appendSpecification(params.filterString, specification)
    });

export const downloadBillableEventsWithSpecification =
  (specification?: BillableEventSpecification): ReturnType<typeof downloadCsv> =>
  async (...params) =>
    downloadCsv('billableEvents')(
      params[0],
      appendSpecification(params[1], specification),
      params[2],
      params[3],
      params[4]
    );

export const getBillableEvent = async (id: number): Promise<BillableEvent | null> => {
  const url = new URL(`/api/billableEvents/${id}`, window.location.origin);
  return await fetchWithRetry<BillableEvent | null>(url, { method: 'GET' }, null);
};

export const getUnitCredits = async (unitId: number): Promise<BillableEvent[]> => {
  const url = new URL(`/api/billableEvents/credit`, window.location.origin);
  url.searchParams.append('unitId', `${unitId}`);
  return await fetchWithRetry<BillableEvent[]>(url, { method: 'GET' }, []);
};

export const postCreateBillableEvent = async (billableEvent: BillableEvent): Promise<BillableEvent | null> => {
  const url = new URL('/api/billableEvents', window.location.origin);
  const { gateInTransaction, gateOutTransaction, ...unit } = { ...billableEvent.unit };
  const opts: RequestInit = {
    method: 'POST',
    headers: new Headers([['Content-Type', 'application/json']]),
    body: JSON.stringify({ ...billableEvent, unit })
  };
  return await fetchWithRetry<BillableEvent | null>(url, opts, null);
};

export const putUpdateBillableEvent = async (
  billableEvent: BillableEvent,
  id: number
): Promise<BillableEvent | null> => {
  const url = new URL(`/api/billableEvents/${id}`, window.location.origin);
  const opts: RequestInit = {
    method: 'PUT',
    headers: new Headers([['Content-Type', 'application/json']]),
    body: JSON.stringify(billableEvent)
  };
  return await fetchWithRetry<BillableEvent | null>(url, opts, null);
};

export const deleteBillableEvent = async (id: number): Promise<void> => {
  const url = new URL(`/api/billableEvents/${id}`, window.location.origin);
  await fetchWithRetry<any>(url, { method: 'DELETE' }, null);
};
